import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField from "../../../components/ui/formelement/v2/EditableField";
import AssetClassificationPill from "./AssetClassificationPill";

import { AssetClassification, UpsertCreditAccountCurrentPositionRequest} from "../../../api/credit_account_current_positions";

import { upsertCreditAccountCurrentPosition} from "../logic/accountDetailsThunk";

import { getAccountDetailByAccountNumber} from "../../creditAccounts/logic/slice";

import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { formatDateStringForDisplay } from "../../../lib/utils";
import { showErrorToast } from "../../../lib/toast";

interface AccountCurrentPositionProps {
  accountNumber: string;
}

const ASSET_CLASSIFICATION_OPTIONS: OptionNameValuePair<AssetClassification>[] = [
  {
    name: "Standard",
    value: AssetClassification.STANDARD,
    display_component: <AssetClassificationPill value={AssetClassification.STANDARD} />,
  },
  {
    name: "SMA 0",
    value: AssetClassification.SMA_0,
    display_component: <AssetClassificationPill value={AssetClassification.SMA_0} />,
  },
  {
    name: "SMA 1",
    value: AssetClassification.SMA_1,
    display_component: <AssetClassificationPill value={AssetClassification.SMA_1} />,
  },
  {
    name: "SMA 2",
    value: AssetClassification.SMA_2,
    display_component: <AssetClassificationPill value={AssetClassification.SMA_2} />,
  },
  {
    name: "Sub Standard",
    value: AssetClassification.SUB_STANDARD,
    display_component: <AssetClassificationPill value={AssetClassification.SUB_STANDARD} />,
  },
  {
    name: "Doubtful",
    value: AssetClassification.DOUBTFUL,
    display_component: <AssetClassificationPill value={AssetClassification.DOUBTFUL} />,
  },
  {
    name: "Loss",
    value: AssetClassification.LOSS,
    display_component: <AssetClassificationPill value={AssetClassification.LOSS} />,
  },
  {
    name: "Written Off",
    value: AssetClassification.WRITTEN_OFF,
    display_component: <AssetClassificationPill value={AssetClassification.WRITTEN_OFF} />,
  },
  {
    name: "Other",
    value: AssetClassification.OTHER,
    display_component: <AssetClassificationPill value={AssetClassification.OTHER} />,
  },
];

const AccountCurrentPosition: React.FC<
  AccountCurrentPositionProps
> = ({ accountNumber }) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const accountDetails = useAppSelector((state) =>
    getAccountDetailByAccountNumber(state, accountNumber)
  );

  if (!accountDetails) {
    return null;
  }

  const currentPosition = accountDetails.accountOverview?.current_position;

  const as_of_date = currentPosition?.as_of_date;
  const asset_classification = currentPosition?.asset_classification;
  const outstanding_balance = currentPosition?.outstanding_balance;
  const principal_outstanding = currentPosition?.principal_outstanding;
  const interest_outstanding = currentPosition?.interest_outstanding;
  const accrued_interest = currentPosition?.accrued_interest;
  const total_dues = currentPosition?.total_dues;
  const overdue_amount = currentPosition?.overdue_amount;
  const remaining_term_months = currentPosition?.remaining_term_months;
  const installments_overdue_months = currentPosition?.installments_overdue_months;
  const days_past_due = currentPosition?.days_past_due;

  const handleSave = async (
    fieldKey: keyof UpsertCreditAccountCurrentPositionRequest,
    value: string | number | null
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      const todayStr = new Date().toISOString().split("T")[0];
      const defaultAsOfDate = as_of_date || todayStr;

      const request: UpsertCreditAccountCurrentPositionRequest = {
        as_of_date: defaultAsOfDate,
        asset_classification: asset_classification ?? null,
        outstanding_balance: outstanding_balance ?? null,
        principal_outstanding: principal_outstanding ?? null,
        interest_outstanding: interest_outstanding ?? null,
        accrued_interest: accrued_interest ?? null,
        total_dues: total_dues ?? null,
        overdue_amount: overdue_amount ?? null,
        remaining_term_months: remaining_term_months ?? null,
        installments_overdue_months: installments_overdue_months ?? null,
        days_past_due: days_past_due ?? null,
      };

      if (fieldKey === "as_of_date") {
        request.as_of_date = String(value ?? "");
      } else if (fieldKey === "asset_classification") {
        request.asset_classification = value
          ? (String(value) as AssetClassification)
          : null;
      } else {
        (request[fieldKey] as number | null) =
          value === null ? null : Number(value);
      }

      await dispatch(
        upsertCreditAccountCurrentPosition({
          caseId: caseId!,
          accountNumber,
          request,
        })
      ).unwrap();

      setEditingField(null);
      return true;
    } catch (error) {
      showErrorToast("Failed to update current position field. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="Account Current Position">
      <div className="flex w-full flex-col gap-2">
        <EditableField
          label="As Of Date"
          value={as_of_date ?? null}
          type="date"
          isEditable={true}
          isEditing={editingField === "as_of_date"}
          onEditStart={() => setEditingField("as_of_date")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("as_of_date", value)}
          displayValue={
            as_of_date ? formatDateStringForDisplay(as_of_date) : "--"
          }
          disabled={isSaving}
        />

        <EditableField
          label="Asset Classification"
          value={asset_classification ?? null}
          type="select"
          isEditable={true}
          options={ASSET_CLASSIFICATION_OPTIONS}
          displayValue={
            asset_classification ? (
              <AssetClassificationPill value={asset_classification} />
            ) : (
              "--"
            )
          }
          isEditing={editingField === "asset_classification"}
          onEditStart={() => setEditingField("asset_classification")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("asset_classification", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Outstanding Amt. (in ₹)"
          value={outstanding_balance ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "outstanding_balance"}
          onEditStart={() => setEditingField("outstanding_balance")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("outstanding_balance", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Principal Outstanding (in ₹)"
          value={principal_outstanding ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "principal_outstanding"}
          onEditStart={() => setEditingField("principal_outstanding")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("principal_outstanding", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Interest Outstanding (in ₹)"
          value={interest_outstanding ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "interest_outstanding"}
          onEditStart={() => setEditingField("interest_outstanding")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("interest_outstanding", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Accrued Interest (in ₹)"
          value={accrued_interest ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "accrued_interest"}
          onEditStart={() => setEditingField("accrued_interest")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("accrued_interest", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Total Dues (in ₹)"
          value={total_dues ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "total_dues"}
          onEditStart={() => setEditingField("total_dues")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("total_dues", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Overdue Amount (in ₹)"
          value={overdue_amount ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "overdue_amount"}
          onEditStart={() => setEditingField("overdue_amount")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("overdue_amount", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Remaining Term (months)"
          value={remaining_term_months ?? null}
          type="integer"
          isEditable={true}
          displayValue={
            remaining_term_months !== null && remaining_term_months !== undefined
              ? String(remaining_term_months)
              : "--"
          }
          isEditing={editingField === "remaining_term_months"}
          onEditStart={() => setEditingField("remaining_term_months")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("remaining_term_months", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Installments Overdue (months)"
          value={installments_overdue_months ?? null}
          type="integer"
          isEditable={true}
          displayValue={
            installments_overdue_months !== null &&
            installments_overdue_months !== undefined
              ? String(installments_overdue_months)
              : "--"
          }
          isEditing={editingField === "installments_overdue_months"}
          onEditStart={() => setEditingField("installments_overdue_months")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("installments_overdue_months", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Days Past Due"
          value={days_past_due ?? null}
          type="integer"
          isEditable={true}
          displayValue={
            days_past_due !== null && days_past_due !== undefined
              ? String(days_past_due)
              : "--"
          }
          isEditing={editingField === "days_past_due"}
          onEditStart={() => setEditingField("days_past_due")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("days_past_due", value)}
          disabled={isSaving}
        />
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default AccountCurrentPosition;